<template>
    <v-dialog
        :value="showDialog"
        persistent
        max-width="400px"
        overlay-color="#909090"
        overlay-opacity="1"
    >
        <v-card>
            <div class="text-right" v-if="!showExitConfirmation">
                <v-btn icon color="primary" @click="showExitConfirmation = true">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <v-card-text
                v-if="!showVerificationDialog && !showExitConfirmation"
                class="pa-3 px-6"
            >
                <div class="text-left" v-if="!showUpdateEmailConfirmation">
                    <v-img
                        src="/images/payment-logos/worldremit.png"
                        contain
                        class="align-self-start mr-auto"
                        height="50"
                    />

                    <h2 class="font-weight-bold primary--text my-5">
                        Enter your credentials
                    </h2>
                    <p class="subtitle-1 my-8" style="line-height: 1.5;">
                        Disclosure about kaoshi connecting and linking their WR app to the Kaoshi app:
                        <span class="font-italic">
                            By providing Kaoshi with your encrypted WorldRemit credentials, you are enabling
                            Kaoshi fill in your transactional and matched peer details on WorldRemit
                        </span>
                    </p>

                    <v-form
                       v-if="!showUpdateEmailConfirmation"
                        class="text-center"
                        validate-on-blur
                        @keyup.enter="login"
                        ref="form"
                        @submit.prevent="login"
                    >
                        <v-text-field
                            aria-required
                            required
                            autofocus
                            type="text"
                            label="Username"
                            name="email"
                            append-icon="mdi-account-outline"
                            background-color="#f8f8fd"
                            color="primary"
                            v-model="email"
                            v-validate="'required'"
                            validate-on-blur
                            outlined
                            :error-messages="errors.first('email')"
                        />

                        <!-- Password -->
                        <v-text-field
                            aria-required
                            required
                            v-model="password"
                            v-validate="'required|verify-password'"
                            validate-on-blur
                            name="password"
                            label="Password"
                            hint="At least 6 characters"
                            outlined
                            background-color="#f8f8fd"
                            color="primary"
                            :append-icon=" passwordVisible ? 'mdi-eye-off' : 'mdi-eye' "
                            @click:append="passwordVisible = !passwordVisible"
                            color:append-icon="primary"
                            :type="passwordVisible ? 'text' : 'password'"
                            :error-messages="errors.first('password')"
                        />

                        <div class="text-center my-4">
                            <v-btn
                                class="text--white text-capitalize py-5 title"
                                color="primary"
                                dark
                                block
                                elevation="2"
                                depressed
                                @click.prevent="submit"
                            >
                                Submit
                            </v-btn>
                        </div>
                    </v-form>
                </div>
                <div class="text-left" v-else>
                    <h2 class="font-weight-bold primary--text text-center my-5">
                        Update Email Address
                    </h2>
                    <div class="mt-5">
                        <p class="subtitle-2 primary--text mt-0 mb-2 font-italic" style="line-height: 24px">
                            Kaoshi is about to update your email address on file with
                            WorldRemit to your assigned kaoshinetwork email address.
                            All of these make your remittance completion swift and seamless.
                        </p>
                        <p class="subtitle-1 mb-1" style="line-height: 1.5;">
                            Benefits include but not limited to
                        </p>
                    </div>
                    <div>
                        <div class="d-flex">
                            <v-icon
                                color="primary"
                                class="mr-1"
                                style="height: 25px"
                            >
                                mdi-check
                            </v-icon>
                            <p class="subtitle-1 primary--text my-0" style="line-height: 20px">
                                Coupons and promo codes tracking which will be
                                applied to your future transactions for discounts
                            </p>
                        </div>
                    </div>
                    <div class="my-2">
                        <div class="d-flex">
                            <v-icon
                                color="primary"
                                class="mr-1"
                                style="height: 25px"
                            >
                                mdi-check
                            </v-icon>
                            <p class="subtitle-1 primary--text my-0">
                                Automated document submissions
                            </p>
                        </div>
                    </div>
                    <div>
                        <div class="d-flex">
                            <v-icon
                                color="primary"
                                class="mr-1"
                                style="height: 25px"
                            >
                                mdi-check
                            </v-icon>
                            <p class="subtitle-1 primary--text my-0">
                                Automated customer care inquiries
                            </p>
                        </div>
                    </div>
                    <p class="subtitle-1 my-2 primary--text" style="line-height: 1.5;">
                        Update email to your kaoshinetwork email?
                    </p>

                    <v-checkbox
                        style="height: 25px"
                        class="my-0"
                        v-model="hideEmailUpdateConfirmation"
                        label="Ok, Please don't show this again."
                    ></v-checkbox>

                    <div class="my-4">
                        <v-btn
                            class="text-none subtitle-1 mb-4"
                            color="primary"
                            dark
                            block
                            elevation="2"
                            depressed
                            @click="confirmUpdateEmail"
                        >
                            Yes, I'll love that
                        </v-btn>
                        <v-btn
                            class="text-none primary--text px-0 subtitle-1 px-2 custom-cancel-button"
                            color="primary"
                            @click="onSuccess"
                            outlined
                            block
                            elevation="2"
                        >
                            No, don't update it
                        </v-btn>
                    </div>
                </div>
            </v-card-text>
            <sms-verification
                v-if="showVerificationDialog && !showExitConfirmation"
                @success="onVerified"
                @error="handleError"
            />
            <exit-confirmation
                v-if="showExitConfirmation"
                @continue="showExitConfirmation = false"
                @exit="decline"
            />
        </v-card>
        <loading-overlay :loading="loading" />
    </v-dialog>
</template>

<script>
import FormFillerService from '../../../services/FormFillerService';
import ExitConfirmation from './exit-confirmation.vue';
import SmsVerification from './sms-verification.vue';

export default {
    components: {
        ExitConfirmation,
        SmsVerification,
    },
    data() {
        return {
            showDialog: false,
            loading: false,
            resolve: () => {},
            showExitConfirmation: false,
            showUpdateEmailConfirmation: false,
            email: null,
            password: null,
            passwordVisible: false,
            showVerificationDialog: false,
        }
    },
    computed: {
        hideEmailUpdateConfirmation: {
            get() {
                return !this.$store.state.WREmailUpdateConfirmation;
            },
            set(value) {
                this.$store.dispatch('updateWREmailUpdateConfirmation', !value);
            },
        },
    },
    methods: {
        show(inputEmail = null) {
            this.showDialog = true;
            this.showExitConfirmation = false;

            if (!inputEmail) {
                this.fetchCredentials();
            } else {
                this.email = inputEmail;
            }

            return new Promise((resolve) => {
                this.resolve = resolve;
                // this.reject = reject;
            });
        },
        async submit() {
            const formIsValid = await this.$validator.validateAll();

            if (formIsValid) {
                await this.login();
                await this.saveCredentials(this.email, this.password);

                const {
                    kaoshi_email: kaoshiEmail,
                } = this.$store.state.user;

                if(!this.hideEmailUpdateConfirmation && this.email !== kaoshiEmail) {
                    this.showUpdateEmailConfirmation = true;
                    return;
                }

                this.onSuccess();
            }
        },
        async onSuccess() {
            this.$swal({
                type: 'success',
                title: 'Account linked successfully!',
            });

            this.showDialog = false;
            this.resolve({});
        },
        async confirmUpdateEmail() {
            this.showVerificationDialog = true;
        },
        async login() {
            try {
                this.loading = true;
                const { data } = await Vue.backendApi().worldremit()
                    .login({
                        email: this.email,
                        password: this.password,
                    });

                this.loading = false;
            } catch (err) {
                this.loading = false;
                this.handleError(err);
                throw err;
            }
        },
        async fetchCredentials() {
            try {
                this.loading = true;
                const {
                    email,
                    password,
                } = await FormFillerService.fetchCredentials('www.worldremit.com');

                this.loading = false;
                this.email = email;
                this.password = password;
            } catch (err) {
                this.loading = false;
                this.handleError(err);
                throw err;
            }
        },
        async saveCredentials(email, password) {
            try {
                this.loading = true;
                await FormFillerService.saveCredentials(email, password, 'www.worldremit.com');
                this.loading = false;
                return password;
            } catch (err) {
                this.loading = false;
                this.handleError(err);
                throw err;
            }
        },
        async onVerified({ mfaId }) {
            const {
                kaoshi_email: kaoshiEmail,
            } = this.$store.state.user;
            await this.updateEmail(mfaId);
            await this.saveCredentials(kaoshiEmail, this.password);

            this.onSuccess();
        },
        async updateEmail(mfaId) {
            try {
                this.loading = true;
                const { data } = await Vue.backendApi().worldremit()
                    .updateEmail({
                        mfaId,
                    });

                this.loading = false;
            } catch (err) {
                this.loading = false;
                this.handleError(err);
                throw err
            }
        },
        handleError(err) {
            this.$swal({
                type: 'error',
                title: 'Something wrong',
                text: err
            });
        },
        decline() {
            this.showDialog = false;
            this.resolve({
              declined: true,
            });
        },
    },
};
</script>

<style scoped>
.custom-cancel-button {
    background-color: #FFEDD3;
}
</style>
